import { TbCheck, TbCopy, TbDownload, TbLoader2, TbX } from "solid-icons/tb";
import { type Component, createSignal, onCleanup, Show } from "solid-js";
import { AddToCanvasMenu, type CanvasTarget } from "./AddToCanvasMenu";
import { MediaLightbox } from "./MediaLightbox";

interface TranscriptMediaProps {
  /** Stamped as `data-media-id` so a host can scroll this row into view by id. */
  mediaId?: string;
  src: string;
  alt?: string;
  /** While true, the image renders dimmed with an uploading spinner and no controls. */
  pending?: boolean;
  /**
   * Puts the image itself — not a link to it — on the clipboard. Rejecting
   * leaves the control unconfirmed; the caller owns saying why it failed.
   */
  onCopy?: () => Promise<void>;
  /** Writes the image to a file. The save dialog (or the browser) is the confirmation. */
  onDownload?: () => Promise<void>;
  /**
   * Control labels. They default to English for Chief, which has no catalog;
   * the web app renders this on a translated page and passes its own.
   */
  copyLabel?: string;
  copiedLabel?: string;
  downloadLabel?: string;
  closeLabel?: string;
  /**
   * When provided, the full-size view offers a control that opens the image for
   * markup. Only a surface with a markup editor supplies it.
   */
  onAnnotate?: () => void;
  /**
   * When provided (and not pending), a hover-revealed remove control is
   * rendered. Removing detaches the image from the transcript; the underlying
   * asset stays in the project library, which the control's copy reflects.
   */
  onRemove?: () => void;
  /** Fires once the image has decoded and laid out, e.g. to re-pin a scroll position. */
  onLoad?: () => void;
  /**
   * Canvases the image can be placed on, offered alongside "New canvas". Only
   * a surface with canvases (Chief) supplies this together with `onAddToCanvas`.
   */
  canvasTargets?: CanvasTarget[];
  /** Places the image on the chosen canvas, or a new one when `targetId` is undefined. */
  onAddToCanvas?: (targetId?: string) => void | Promise<void>;
}

/** How long the copy control stays confirmed after a successful write. */
const COPIED_MS = 1600;

// Presentational only: it renders an inline transcript image and, optionally, a
// remove control. It is agnostic to where `src` came from (a data: URL in the
// demo, a signed GET URL in Chief), so both surfaces share one render.
export const TranscriptMedia: Component<TranscriptMediaProps> = (props) => {
  const [previewOpen, setPreviewOpen] = createSignal(false);
  const [copied, setCopied] = createSignal(false);
  const [menuOpen, setMenuOpen] = createSignal(false);

  let copiedTimer: number | undefined;
  onCleanup(() => clearTimeout(copiedTimer));

  const copy = async (): Promise<void> => {
    try {
      await props.onCopy?.();
    } catch {
      return;
    }
    setCopied(true);
    clearTimeout(copiedTimer);
    copiedTimer = window.setTimeout(() => setCopied(false), COPIED_MS);
  };

  const copyLabel = () => props.copyLabel ?? "Copy image";
  const copiedLabel = () => props.copiedLabel ?? "Copied";
  const downloadLabel = () => props.downloadLabel ?? "Download image";

  // Swallowed rather than left to `void`, where an unhandled rejection would
  // reach the app's error monitoring. Chief reports the failure in the
  // transcript's error strip; the web page has no error surface of its own.
  const download = async (): Promise<void> => {
    try {
      await props.onDownload?.();
    } catch {
      /* the caller reports it */
    }
  };

  return (
    <div data-media-id={props.mediaId} class="group/media relative my-2 inline-block max-w-full">
      <button
        type="button"
        aria-label="View full size"
        // A pending image is a local object URL that gets swapped (and revoked)
        // once the upload lands, so there is nothing stable to blow up yet.
        disabled={props.pending}
        onClick={() => setPreviewOpen(true)}
        class="block max-w-full cursor-zoom-in rounded-lg focus:ring-2 focus:ring-brand focus:outline-none disabled:cursor-default"
      >
        <img
          src={props.src}
          alt={props.alt ?? ""}
          onLoad={() => props.onLoad?.()}
          classList={{ "opacity-60": props.pending }}
          class="max-h-80 max-w-full rounded-lg border border-primary-border object-contain transition-opacity"
        />
      </button>
      <Show when={previewOpen()}>
        <MediaLightbox
          src={props.src}
          alt={props.alt}
          copied={copied()}
          copyLabel={copyLabel()}
          copiedLabel={copiedLabel()}
          downloadLabel={downloadLabel()}
          closeLabel={props.closeLabel}
          onCopy={props.onCopy && (() => void copy())}
          onDownload={props.onDownload && (() => void download())}
          // The editor is the surface the user asked for, so the overlay steps
          // out of its way rather than stacking a second Escape handler over it.
          onAnnotate={
            props.onAnnotate &&
            (() => {
              setPreviewOpen(false);
              props.onAnnotate?.();
            })
          }
          canvasTargets={props.canvasTargets}
          onAddToCanvas={
            props.onAddToCanvas &&
            ((id) => {
              setPreviewOpen(false);
              return props.onAddToCanvas?.(id);
            })
          }
          onClose={() => setPreviewOpen(false)}
        />
      </Show>
      <Show when={props.pending}>
        <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span class="rounded-full bg-canvas/70 p-1.5">
            <TbLoader2 class="size-5 animate-spin text-primary-text" />
          </span>
        </div>
      </Show>
      <Show when={!props.pending}>
        {/* Revealed on focus as well as hover, so the controls are reachable from the keyboard.
            Stays flexed while the (portaled) canvas menu is open, since that moves focus outside this group. */}
        <div
          class={`absolute top-1.5 right-1.5 items-center gap-0.5 rounded-full border border-primary-border bg-canvas/90 p-0.5 group-focus-within/media:flex group-hover/media:flex ${menuOpen() ? "flex" : "hidden"}`}
        >
          <Show when={props.onCopy}>
            <button
              type="button"
              aria-label={copied() ? copiedLabel() : copyLabel()}
              title={copied() ? copiedLabel() : copyLabel()}
              onClick={() => void copy()}
              class="rounded-full p-1 text-primary-text hover:bg-panel-hover focus:ring-2 focus:ring-brand focus:outline-none"
            >
              <Show when={copied()} fallback={<TbCopy class="size-4" />}>
                <TbCheck class="size-4 text-accent" />
              </Show>
            </button>
          </Show>
          <Show when={props.onDownload}>
            <button
              type="button"
              aria-label={downloadLabel()}
              title={downloadLabel()}
              onClick={() => void download()}
              class="rounded-full p-1 text-primary-text hover:bg-panel-hover focus:ring-2 focus:ring-brand focus:outline-none"
            >
              <TbDownload class="size-4" />
            </button>
          </Show>
          <Show when={props.onAddToCanvas && props.canvasTargets}>
            <AddToCanvasMenu
              targets={props.canvasTargets ?? []}
              onAdd={(id) => props.onAddToCanvas?.(id)}
              triggerClass="rounded-full p-1 text-primary-text hover:bg-panel-hover focus:outline-none focus:ring-2 focus:ring-brand"
              onOpenChange={setMenuOpen}
            />
          </Show>
          <Show when={props.onRemove}>
            {/* Kept behind a divider: removing is the one action here that changes the session. */}
            <span class="mx-0.5 h-4 w-px bg-primary-border" />
            <button
              type="button"
              aria-label="Remove from session"
              title="Remove from session"
              onClick={() => props.onRemove?.()}
              class="rounded-full p-1 text-primary-text hover:bg-panel-hover focus:ring-2 focus:ring-brand focus:outline-none"
            >
              <TbX class="size-4" />
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
};
